import { useRef, useState } from 'react'
import { motion, useInView, AnimatePresence } from 'framer-motion'
import { Star, Quote, ChevronLeft, ChevronRight } from 'lucide-react'

const testimonials = [
  {
    id: 1,
    name: 'Sarah Mitchell',
    role: 'Homeowner',
    location: 'Jacksonville, FL',
    text: 'Emeralds Cuts transformed our yard completely! Our lawn went from patchy to the greenest on the block.',
    service: 'Full Lawn Care',
  },
  {
    id: 2,
    name: 'James Rodriguez',
    role: 'Property Manager',
    location: 'Orange Park, FL',
    text: 'We manage 12 commercial properties and Emeralds Cuts handles all of them flawlessly. An invaluable partner.',
    service: 'Commercial Maintenance',
  },
  {
    id: 3,
    name: 'Emily Chen',
    role: 'Homeowner',
    location: 'St. Johns, FL',
    text: 'The landscaping team designed a beautiful garden that exceeded our expectations. Incredible attention to detail.',
    service: 'Landscape Design',
  },
]

export default function Testimonials() {
  const ref = useRef()
  const isInView = useInView(ref, { once: true, margin: '-100px' })
  const [activeIndex, setActiveIndex] = useState(0)
  const [direction, setDirection] = useState(0)

  const navigate = (dir) => {
    setDirection(dir)
    setActiveIndex((prev) => {
      if (dir === 1) return prev === testimonials.length - 1 ? 0 : prev + 1
      return prev === 0 ? testimonials.length - 1 : prev - 1
    })
  }

  return (
    <section id="testimonials" className="section py-24 bg-white">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" ref={ref}>
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <span className="inline-flex items-center justify-center gap-2 px-4 py-2 bg-[#2d7d32]/10 text-[#2d7d32] rounded-full text-sm font-semibold mb-4">
            <Star className="w-4 h-4" />
            Reviews
          </span>
          <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-4">
            What Our <span className="gradient-text">Clients Say</span>
          </h2>
        </motion.div>

        {/* Testimonial Carousel */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="relative"
        >
          <div className="bg-[#f8faf7] rounded-2xl p-8 sm:p-12 min-h-[300px] flex items-center justify-center">
            <AnimatePresence mode="wait" custom={direction}>
              <motion.div
                key={activeIndex}
                custom={direction}
                initial={{ opacity: 0, x: direction > 0 ? 100 : -100 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: direction > 0 ? -100 : 100 }}
                transition={{ duration: 0.3 }}
                className="w-full text-center"
              >
                <Quote className="w-10 h-10 text-[#2d7d32]/20 mx-auto mb-6" />

                <div className="flex items-center justify-center gap-1 mb-6">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <Star key={i} className="w-5 h-5 text-yellow-400 fill-yellow-400" />
                  ))}
                </div>

                <blockquote className="text-lg text-gray-700 mb-8 max-w-2xl mx-auto">
                  "{testimonials[activeIndex].text}"
                </blockquote>

                <div>
                  <div className="font-bold text-gray-900">{testimonials[activeIndex].name}</div>
                  <div className="text-sm text-gray-500">
                    {testimonials[activeIndex].role} • {testimonials[activeIndex].location}
                  </div>
                  <div className="text-xs text-[#2d7d32] font-medium mt-1">
                    {testimonials[activeIndex].service}
                  </div>
                </div>
              </motion.div>
            </AnimatePresence>

            {/* Navigation */}
            <button
              onClick={() => navigate(-1)}
              className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-white rounded-full shadow flex items-center justify-center text-gray-600 hover:text-[#2d7d32]"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <button
              onClick={() => navigate(1)}
              className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-white rounded-full shadow flex items-center justify-center text-gray-600 hover:text-[#2d7d32]"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>

          {/* Dots */}
          <div className="flex items-center justify-center gap-2 mt-6">
            {testimonials.map((_, i) => (
              <button
                key={i}
                onClick={() => {
                  setDirection(i > activeIndex ? 1 : -1)
                  setActiveIndex(i)
                }}
                className={`w-2.5 h-2.5 rounded-full transition-all ${
                  i === activeIndex ? 'w-8 bg-[#2d7d32]' : 'bg-gray-300'
                }`}
              />
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  )
}
